import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../utils/Auth/AuthProvider';
import {
  useAddGrade,
  useAddSubject,
  useSchoolGradeSubjects,
  useSchoolGrades,
  useSubjectsList,
  useSearchGrades,
  useJoinGrade,
} from '../../react-query/queries';
import { useQuery } from '@tanstack/react-query';
import API from '../../utils/constant';
import {
  useSectionsList,
  useTeacherAssignmentCreate,
  useTeacherAssignments,
} from '../../school_panel/api/hooks';
import { SchoolApi } from '../../school_panel/api/client';
import { LINK_BTN } from '../../school_panel/components/ui/linkButton';
import { BlueprintCard, RemoveDialog, RemoveTarget } from './blueprint';
import { Button, Chip, EmptyState, FormField, Initials, Modal, PageHeader, PagedFooter, PanelCard, Spinner, TextLink, TextLinkButton, Track } from '../../school_panel/components/ui';
import SetupSteps, { setupStepTotal } from '../../school_panel/components/ui/SetupSteps';
import { FaArrowRight, FaBookOpen, FaChevronDown, FaChevronRight, FaPlus } from '../../school_panel/components/ui/icons';
import { CalendarDays, Check, Landmark, ListFilter, X } from 'lucide-react';
import { fieldClass, NAME_MAX } from '../../school_panel/lib/fieldClass';
import { apiErrorText } from '../../school_panel/lib/apiError';
import { teacherName, type TeacherProfile } from '../../school_panel/lib/teachers';
import RollNumbersModal from '../../school_panel/pages/paper_exams/components/RollNumbersModal';
import {
  getUserInfo,
  adminGetInstitutionFields,
  AdminField,
  adminGetFieldAcademicYears,
  adminCreateAcademicYear,
  AdminAcademicYear,
  adminGetAcademicYearCourses,
  adminAddInstitutionField,
  searchFields,
  adminCreateAcademicYearCourse,
} from '../../utils/remote';
import { getPresetInstitutionGradeSubjects } from '../../utils/remote';
import { Link, useNavigate } from 'react-router-dom';
import { gradeLabel } from '../../school_panel/lib/gradeLabel';
import usePagination from '../../school_panel/lib/usePagination';

const ClassSetup = () => {
  const { token, role } = useAuth();
  const isAllowed =
    !!token &&
    (role === 'superuser' ||
      role === 'school' ||
      role === 'school_admin' ||
      role === 'admin' ||
      role === 'college_university');
  const [gradeId, setGradeId] = useState<number | null>(null);
  const [newGrade, setNewGrade] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [newSubject, setNewSubject] = useState('');
  const [isGradeModalOpen, setIsGradeModalOpen] = useState(false);
  const [isSubjectModalOpen, setIsSubjectModalOpen] = useState(false);
  const [isPresetModalOpen, setIsPresetModalOpen] = useState(false);
  // The grade the open preset drawer was loaded for; import and late responses use this, never the current selection.
  const presetGradeRef = useRef<number | null>(null);
  const [isCourseModalOpen, setIsCourseModalOpen] = useState(false);
  const [newCourseName, setNewCourseName] = useState('');
  const [selectedYearId, setSelectedYearId] = useState<number | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [filteredSuggestions, setFilteredSuggestions] = useState<any[]>([]);
  const [gradeSearchTerm, setGradeSearchTerm] = useState('');
  const [gradeSuggestions, setGradeSuggestions] = useState<any[]>([]);
  const [selectedSubject, setSelectedSubject] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [presetSubjects, setPresetSubjects] = useState<
    { id: number; name: string; institutions?: number[] }[]
  >([]);
  const [selectedPresetIds, setSelectedPresetIds] = useState<number[]>([]);
  const [presetLoading, setPresetLoading] = useState(false);
  const [presetError, setPresetError] = useState<string | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [gradeError, setGradeError] = useState<string | null>(null);
  const [subjectError, setSubjectError] = useState<string | null>(null);
  const [courseError, setCourseError] = useState<string | null>(null);
  const [homeroomError, setHomeroomError] = useState<string | null>(null);
  const [courseBusy, setCourseBusy] = useState(false);
  const [yearBusy, setYearBusy] = useState(false);
  const navigate = useNavigate();
  const [removeTarget, setRemoveTarget] = useState<RemoveTarget | null>(null);

  const {
    data: gradesData,
    isFetching: gradesLoading,
    refetch: refetchGrades,
  } = useSchoolGrades(token);
  const {
    data: subsData,
    isFetching: subsLoading,
    refetch: refetchSubs,
  } = useSchoolGradeSubjects(token, gradeId ? String(gradeId) : '0');
  const { data: allSubjectsData } = useSubjectsList(token);
  const addGrade = useAddGrade();
  const searchGrades = useSearchGrades();
  const joinGrade = useJoinGrade();
  const addSubject = useAddSubject();

  // Homeroom teacher assignment hooks
  const teacherAssignments = useTeacherAssignments();
  const homeroomCreate = useTeacherAssignmentCreate();
  const teacherProfiles = useQuery({
    queryKey: ['school', 'teacher_profiles'],
    queryFn: () =>
      API.get<{ teachers: TeacherProfile[] }>(
        '/api/school/teachers/',
      ).then((r) => r.data.teachers ?? []),
  });
  const analyticsQ = useQuery({
    queryKey: ['analytics.grades'],
    queryFn: () => SchoolApi.analyticsGrades(),
    enabled: isAllowed,
  });
  const analyticsGrades = analyticsQ.data?.grades ?? [];
  const [changingHomeroomGradeId, setChangingHomeroomGradeId] = useState<number | null>(null);
  const [rollNumbersGrade, setRollNumbersGrade] = useState<{ id: number; name: string } | null>(null);
  const [userInfo, setUserInfo] = useState<any>(null);
  const [fieldsData, setFieldsData] = useState<AdminField[] | null>(null);
  const [academicYears, setAcademicYears] = useState<
    AdminAcademicYear[] | null
  >(null);
  const [newAcademicYearName, setNewAcademicYearName] = useState('');
  const [expandedYearId, setExpandedYearId] = useState<number | null>(null);
  const [coursesByYear, setCoursesByYear] = useState<
    Record<number, { id: number; name: string }[]>
  >({});
  const [loadingYearCourses, setLoadingYearCourses] = useState<number | null>(
    null,
  );

  const isUniversity = (() => {
    const instType =
      userInfo?.institution_type ??
      (role === 'college_university' ? 'University/College' : '');
    const t = String(instType || '').toLowerCase();
    return (
      t.includes('university') ||
      t.includes('college') ||
      role === 'college_university'
    );
  })();

  const sectionsQ = useSectionsList(isAllowed && !isUniversity ? gradeId : null);

  // Picking another grade closes the preset drawer: its list, "Added" marks and selection belong to the old grade.
  useEffect(() => {
    presetGradeRef.current = null;
    setIsPresetModalOpen(false);
    setPresetSubjects([]);
    setSelectedPresetIds([]);
    setPresetError(null);
    setImportError(null);
    setPresetLoading(false);
  }, [gradeId]);

  useEffect(() => {
    if (gradesData?.grades?.length && gradeId == null) {
      setGradeId(gradesData.grades[0].id);
    }
  }, [gradesData, gradeId]);

  useEffect(() => {
    if (searchTerm && allSubjectsData?.subjects) {
      const filtered = allSubjectsData.subjects.filter((s: any) =>
        s.name.toLowerCase().includes(searchTerm.toLowerCase()),
      );
      setFilteredSuggestions(filtered);
    } else {
      setFilteredSuggestions([]);
    }
  }, [searchTerm, allSubjectsData]);

  // Debounced grade search
  useEffect(() => {
    const t = setTimeout(async () => {
      if (!isGradeModalOpen) return;
      try {
        const q = gradeSearchTerm.trim();
        if (isUniversity) {
          if (q) {
            const res = await searchFields(token, q);
            setGradeSuggestions(res || []);
          } else {
            setGradeSuggestions([]);
          }
        } else {
          const res = await searchGrades.mutateAsync({
            toBeValidated: token,
            q,
          });
          setGradeSuggestions(Array.isArray(res) ? res : []);
        }
      } catch (e) {
        setGradeSuggestions([]);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [
    gradeSearchTerm,
    isGradeModalOpen,
    isUniversity,
    token,
    searchGrades.mutateAsync,
  ]);

  useEffect(() => {
    const fetch = async () => {
      try {
        const data = await getUserInfo();
        setUserInfo(data);
      } catch (e) {
        // ignore
      }
    };
    fetch();
  }, []);

  useEffect(() => {
    let mounted = true;
    const loadFields = async () => {
      try {
        const instType =
          userInfo?.institution_type ||
          (role === 'school'
            ? 'School'
            : role === 'college_university'
            ? 'University/College'
            : null);
        const instId = userInfo?.institution_id ?? null;
        if (
          instId &&
          instType &&
          (String(instType).toLowerCase().includes('university') ||
            String(instType).toLowerCase().includes('college'))
        ) {
          const fields = await adminGetInstitutionFields(instId);
          if (!mounted) return;
          setFieldsData(fields || []);
          if ((fields || []).length && gradeId == null)
            setGradeId((fields[0] && fields[0].id) || null);
        } else {
          if (mounted) setFieldsData(null);
        }
      } catch (e) {
        if (mounted) setFieldsData([]);
      }
    };
    loadFields();
    return () => {
      mounted = false;
    };
  }, [userInfo, role, gradeId]); // Added gradeId dependency to satisfy linter if needed, but logic handles null check. Retaining similar dependencies.

  useEffect(() => {
    let mounted = true;
    const instId = userInfo?.institution_id ?? null;
    if (isUniversity && instId && gradeId) {
      (async () => {
        try {
          const years = await adminGetFieldAcademicYears(instId, gradeId);
          if (!mounted) return;
          setAcademicYears(years || []);
        } catch (e) {
          if (mounted) setAcademicYears([]);
        }
      })();
    } else {
      if (mounted) setAcademicYears(null);
    }
    return () => {
      mounted = false;
    };
  }, [isUniversity, userInfo, gradeId]);

  const gradesPg = usePagination<any>(gradesData?.grades ?? [], 10);
  const fieldsPg = usePagination<AdminField>(fieldsData ?? [], 10);
  const activeGrades = isUniversity ? fieldsPg : gradesPg;
  const subjectsPg = usePagination<any>(subsData?.subjects ?? [], 12, gradeId);

  if (!isAllowed)
    return (
      <div role="alert" className="rounded-[12px] border border-danger/30 bg-danger/10 p-4 text-sm text-danger">
        You don't have access to class setup.
      </div>
    );

  const selectedGradeName = (() => {
    if (isUniversity) return fieldsData?.find((f) => f.id === gradeId)?.name || '';
    const g = gradesData?.grades?.find((x: any) => x.id === gradeId);
    return g ? gradeLabel(g.grade_name || g.name) : '';
  })();

  const submitGrade = async () => {
    if (!newGrade.trim() || addGrade.isPending || joinGrade.isPending) return;
    setGradeError(null);
    const term = newGrade.trim();
    const matched = gradeSuggestions.find(
      (g) =>
        (g.name || g.grade_name || '').toLowerCase() === term.toLowerCase(),
    );

    try {
      if (isUniversity) {
        const instId = userInfo?.institution_id;
        if (instId) {
          await adminAddInstitutionField(instId, term);
          const fields = await adminGetInstitutionFields(instId);
          setFieldsData(fields || []);
        }
      } else {
        if (matched) {
          try {
            await joinGrade.mutateAsync({
              toBeValidated: token,
              gradeId: matched.id,
            });
          } catch (e) {
            await addGrade.mutateAsync({ toBeValidated: token, name: term });
          }
        } else {
          await addGrade.mutateAsync({ toBeValidated: token, name: term });
        }
        refetchGrades();
      }
    } catch (e) {
      setGradeError(apiErrorText(e, `Could not add the ${isUniversity ? 'department' : 'grade'}. Try again.`));
      return;
    }
    setNewGrade('');
    setIsGradeModalOpen(false);
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);
      setGradeError(null);
      const text = await file.text();
      const json = JSON.parse(text);

      const items = json.universities || json.departments || [];
      if (!Array.isArray(items)) {
        throw new Error(
          "Invalid JSON format. Expected an array in 'universities'.",
        );
      }

      if (isUniversity) {
        const instId = userInfo?.institution_id;
        if (instId) {
          for (const item of items) {
            const name = item.name || item;
            if (name) {
              await adminAddInstitutionField(instId, name);
            }
          }
          const fields = await adminGetInstitutionFields(instId);
          setFieldsData(fields || []);
        }
      } else {
        for (const item of items) {
          const name = item.name || item;
          if (name) {
            await addGrade.mutateAsync({ toBeValidated: token, name: name });
          }
        }
        refetchGrades();
      }
      setIsGradeModalOpen(false);
    } catch (e) {
      setGradeError(
        'Could not read that file. Expected format: { "departments": [ { "name": "..." } ] }',
      );
    } finally {
      setIsUploading(false);
      if (event.target) event.target.value = '';
    }
  };

  const submitSubject = async () => {
    if (!gradeId) return;

    if (selectedSubject) {
      await addSubject.mutateAsync({
        toBeValidated: token,
        gradeId: gradeId,
        subjectId: selectedSubject.id,
      });
    } else if (newSubject.trim()) {
      await addSubject.mutateAsync({
        toBeValidated: token,
        gradeId: gradeId,
        name: newSubject.trim(),
      });
    } else {
      return;
    }

    setNewSubject('');
    setSearchTerm('');
    setSelectedSubject(null);
    refetchSubs();
    setIsSubjectModalOpen(false);
  };

  const handleSubjectSelect = (subject: { id: number; name: string }) => {
    setSelectedSubject(subject);
    setSearchTerm(subject.name);
    setNewSubject(subject.name);
    setFilteredSuggestions([]);
  };

  const goToChapters = (subjectId: number) => {
    const params = new URLSearchParams();
    params.set('subjectId', String(subjectId));
    if (gradeId) params.set('gradeId', String(gradeId));
    navigate(`/school-setup/chapters?${params.toString()}`);
  };

  const loadPresets = async () => {
    if (!gradeId) return;
    const instType =
      userInfo?.institution_type ||
      (role === 'school'
        ? 'School'
        : role === 'college_university'
        ? 'University/College'
        : null);
    const presetName =
      instType && String(instType).toLowerCase().includes('school')
        ? 'eltest-school'
        : 'eltest-uni';
    setPresetSubjects([]);
    setSelectedPresetIds([]);
    setPresetError(null);
    setImportError(null);
    setPresetLoading(true);
    setIsPresetModalOpen(true);
    presetGradeRef.current = gradeId;
    const forGrade = gradeId;
    try {
      const items = await getPresetInstitutionGradeSubjects(presetName, gradeId);
      if (presetGradeRef.current === forGrade) setPresetSubjects(items || []);
    } catch (e) {
      // Distinct from an empty list: the request itself failed.
      if (presetGradeRef.current === forGrade) setPresetError(apiErrorText(e, 'The preset list could not be loaded.'));
    } finally {
      // A stale response (another grade was picked meanwhile) must not clear the new load's flag.
      if (presetGradeRef.current === forGrade) setPresetLoading(false);
    }
  };

  const submitCourse = async () => {
    if (!gradeId || !selectedYearId || !newCourseName.trim() || courseBusy) return;
    const instId = userInfo?.institution_id;
    if (!instId) return;
    setCourseBusy(true);
    setCourseError(null);
    try {
      await adminCreateAcademicYearCourse(instId, gradeId, selectedYearId, newCourseName.trim());
      const updatedCourses = await adminGetAcademicYearCourses(instId, gradeId, selectedYearId);
      setCoursesByYear((prev) => ({ ...prev, [selectedYearId]: updatedCourses || [] }));
      setNewCourseName('');
      setIsCourseModalOpen(false);
    } catch (e) {
      setCourseError(apiErrorText(e, 'Could not add the course. Try again.'));
    } finally {
      setCourseBusy(false);
    }
  };

  const submitSubjectForm = async () => {
    if (!gradeId || addSubject.isPending || yearBusy) return;
    setSubjectError(null);
    if (isUniversity) {
      if (!newAcademicYearName.trim()) return;
      const instId = userInfo?.institution_id;
      if (!instId) return;
      setYearBusy(true);
      try {
        await adminCreateAcademicYear(instId, gradeId, newAcademicYearName.trim());
        const years = await adminGetFieldAcademicYears(instId, gradeId);
        setAcademicYears(years || []);
        setNewAcademicYearName('');
        setIsSubjectModalOpen(false);
      } catch (e) {
        setSubjectError(apiErrorText(e, 'Could not add the academic year. Try again.'));
      } finally {
        setYearBusy(false);
      }
      return;
    }
    try {
      for (const sid of selectedPresetIds) {
        await addSubject.mutateAsync({ toBeValidated: token, gradeId, subjectId: sid });
      }
      await submitSubject();
    } catch (e) {
      setSubjectError(apiErrorText(e, 'Could not add the subject. Try again.'));
    }
  };

  const importPresets = async () => {
    const gradeId = presetGradeRef.current;
    if (!gradeId || addSubject.isPending) return;
    const importedFor = gradeId;
    setImportError(null);
    const failedIds: number[] = [];
    for (const sid of selectedPresetIds) {
      try {
        await addSubject.mutateAsync({ toBeValidated: token, gradeId, subjectId: sid });
      } catch (e) {
        failedIds.push(sid);
      }
    }
    refetchSubs();
    if (failedIds.length) {
      // Another grade was picked mid-import: only report, never re-select the old grade's ids.
      const sameGrade = presetGradeRef.current === importedFor;
      if (sameGrade) setSelectedPresetIds(failedIds);
      setImportError(
        `Couldn't import: ${failedIds.map((id) => presetSubjects.find((p) => p.id === id)?.name ?? id).join(', ')}.`,
      );
      return;
    }
    if (presetGradeRef.current !== importedFor) return;
    setSelectedPresetIds([]);
    setIsPresetModalOpen(false);
  };

  const toggleYear = async (y: AdminAcademicYear) => {
    if (expandedYearId === y.id) {
      setExpandedYearId(null);
      return;
    }
    setExpandedYearId(y.id);
    if (!coursesByYear[y.id]) {
      const instId = userInfo?.institution_id;
      if (!instId) return;
      try {
        setLoadingYearCourses(y.id);
        const courses = await adminGetAcademicYearCourses(instId, gradeId as number, y.id);
        setCoursesByYear((prev) => ({ ...prev, [y.id]: courses || [] }));
      } catch (e) {
        setCoursesByYear((prev) => ({ ...prev, [y.id]: [] }));
      } finally {
        setLoadingYearCourses(null);
      }
    }
  };

  const selectableIds = presetSubjects
    .filter((p) => !(userInfo?.institution_id && Array.isArray(p.institutions) && p.institutions.includes(userInfo.institution_id)))
    .map((p) => p.id);
  const allPresetsSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedPresetIds.includes(id));

  const homeroom = (teacherAssignments.data ?? []).find(
    (a) => a.school_grade === gradeId && a.is_homeroom,
  );
  const isChangingHomeroom = changingHomeroomGradeId === gradeId;

  const subjectSaveDisabled = isUniversity
    ? !newAcademicYearName.trim()
    : !selectedSubject && !newSubject.trim() && selectedPresetIds.length === 0;

  const analyticsGrade = (id: number) => analyticsGrades.find((g) => g.id === id);
  const gradeBadge = (label: string) => {
    const bare = label.replace(/^grade\s+/i, '').trim();
    return (bare.length <= 3 ? bare : bare.slice(0, 2)).toUpperCase();
  };
  const personName = (t: { first_name?: string; last_name?: string; username?: string } | null | undefined) =>
    t ? [t.first_name, t.last_name].filter(Boolean).join(' ') || t.username || '' : '';
  const teachersFor = (subjectId: number): string => {
    const names = (teacherAssignments.data ?? [])
      .filter((a) => a.subject === subjectId && a.school_grade === gradeId && !a.is_homeroom)
      .map((a) => a.teacher_name || a.teacher_username);
    return Array.from(new Set(names)).join(', ');
  };
  // Share of the subject's chapters that have at least one tagged question; null with no chapters.
  const coverage = (sub: { chapter_count?: number; tagged_chapter_count?: number }) =>
    sub.chapter_count ? Math.round(((sub.tagged_chapter_count ?? 0) / sub.chapter_count) * 100) : null;
  const gradeCount = isUniversity ? fieldsData?.length ?? 0 : gradesData?.grades?.length ?? 0;
  const sectionCount = analyticsGrades.reduce((n, g) => n + g.sections.length, 0);
  // Subjects per grade come from the analytics payload; older backends omit the field, so no total is claimed.
  const subjectTotal =
    analyticsGrades.length > 0 && analyticsGrades.every((g) => typeof g.subject_count === 'number')
      ? analyticsGrades.reduce((n, g) => n + (g.subject_count ?? 0), 0)
      : null;
  const subtitle = isUniversity
    ? 'Departments, academic years and courses for your institution.'
    : `${analyticsQ.data ? `${gradeCount} grades · ${sectionCount} sections${subjectTotal != null ? ` · ${subjectTotal} subjects` : ''}. ` : ''}Grades and the subjects taught in each. Chapters are set up next.`;

  // Design `.gbtn`: 12px padding, 14px radius; the selected row is the ground fill with a line-strong border.
  const rowBase =
    'flex w-full min-w-0 items-center gap-3 rounded-[14px] border p-3 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent';
  const rowOn = (on: boolean) => (on ? 'border-line-strong bg-ground' : 'border-transparent hover:bg-panel');
  const badgeOn = (on: boolean) =>
    `grid h-[42px] w-[42px] flex-none place-items-center rounded-[13px] font-display text-base font-bold ${
      on ? 'bg-navy text-white dark:bg-mist-fixed dark:text-navy' : 'bg-mist text-navy dark:text-ink'}`;
  const sectionLabel = 'font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted';

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        eyebrow={`Setup · Step 1 of ${setupStepTotal(role)}`}
        title="Classes"
        subtitle={subtitle}
        actions={
          <>
            <Link
              to="/school-setup/chapters"
              className={LINK_BTN}>
              Continue to chapters
              <FaArrowRight size={16} strokeWidth={1.8} aria-hidden />
            </Link>
            <Button icon={<FaPlus />} onClick={() => { setGradeError(null); setIsGradeModalOpen(true); }}>
              {isUniversity ? 'Add department' : 'Add grade'}
            </Button>
          </>
        }
      />

      <SetupSteps current={1} />

      {!isUniversity && !gradesLoading && gradesData && !(gradesData.grades?.length) && (
        <div className="mb-[18px]"><BlueprintCard onApplied={() => { refetchGrades(); refetchSubs(); }} /></div>
      )}

      <div className="grid grid-cols-1 gap-[18px] lg:grid-cols-[minmax(0,4fr)_minmax(0,8fr)]">
        {/* Grades / Departments column */}
        <PanelCard
          title={isUniversity ? 'Departments' : 'Grades'}
          subtitle={isUniversity ? 'Pick a department to edit its years' : 'Pick a grade to edit its subjects'}
          actions={<Chip tone="info">{gradeCount} total</Chip>}
          className="h-fit">
          <div className="max-h-[420px] overflow-y-auto">
            {isUniversity ? (
              fieldsData === null ? (
                <div className="flex justify-center p-8"><Spinner size="md" /></div>
              ) : fieldsData.length ? (
                <ul className="flex flex-col gap-1.5">
                  {fieldsPg.pageItems.map((f: AdminField) => (
                    <li key={f.id}>
                      <button type="button" onClick={() => setGradeId(f.id)} aria-current={gradeId === f.id ? 'true' : undefined} className={`${rowBase} ${rowOn(gradeId === f.id)}`}>
                        <span className={badgeOn(gradeId === f.id)}>{gradeBadge(f.name)}</span>
                        <span className="min-w-0 flex-1 break-words font-semibold text-ink">{f.name}</span>
                        <FaChevronRight className={gradeId === f.id ? 'text-ink' : 'text-muted'} />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <EmptyState icon={<Landmark size={18} strokeWidth={1.8} />} title="No departments yet" body="Use Add department to create the first one." />
              )
            ) : gradesLoading ? (
              <div className="flex justify-center p-8"><Spinner size="md" /></div>
            ) : gradesData?.grades?.length ? (
              <ul className="flex flex-col gap-1.5">
                {gradesPg.pageItems.map((g: any) => {
                  const label = gradeLabel(g.grade_name || g.name);
                  const stats = analyticsGrade(g.id);
                  return (
                    <li key={g.id}>
                      <button type="button" onClick={() => setGradeId(g.id)} aria-current={gradeId === g.id ? 'true' : undefined} className={`${rowBase} ${rowOn(gradeId === g.id)}`}>
                        <span className={badgeOn(gradeId === g.id)}>{gradeBadge(label)}</span>
                        <span className="min-w-0 flex-1">
                          <span className="block break-words font-semibold text-ink">{label}</span>
                          {stats && (
                            <span className="block font-mono text-[11px] font-semibold text-muted">
                              {stats.student_count} students · {stats.sections.length} sections
                              {typeof stats.subject_count === 'number' ? ` · ${stats.subject_count} subjects` : ''}
                            </span>
                          )}
                        </span>
                        <FaChevronRight className="text-muted" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <EmptyState icon={<Landmark size={18} strokeWidth={1.8} />} title="No grades yet" body="Use Add grade to create the first one." />
            )}
          </div>
          {activeGrades.pageCount > 1 && (
            <PagedFooter
              from={activeGrades.from}
              to={activeGrades.to}
              total={activeGrades.total}
              page={activeGrades.page}
              pageCount={activeGrades.pageCount}
              onPage={activeGrades.setPage}
              className="mt-3"
            />
          )}

          {/* Homeroom + roll numbers: school mode only, shown when a grade is selected */}
          {!isUniversity && gradeId != null && (
            <div className="mt-4">
              <div className="rounded-[16px] border border-line bg-panel p-3.5">
                <div className="flex items-center justify-between gap-2.5">
                  <span className={sectionLabel}>Homeroom lead · {selectedGradeName}</span>
                  {homeroom && !isChangingHomeroom ? (
                    <TextLinkButton className="-mr-2" onClick={() => setChangingHomeroomGradeId(gradeId)}>Change</TextLinkButton>
                  ) : isChangingHomeroom ? (
                    <TextLinkButton className="-mr-2" onClick={() => setChangingHomeroomGradeId(null)}>Cancel</TextLinkButton>
                  ) : null}
                </div>
                {homeroom && !isChangingHomeroom ? (
                  <div className="mt-2.5 flex items-center gap-3">
                    <Initials name={(homeroom as any).teacher_name || homeroom.teacher_username} />
                    <span className="min-w-0 flex-1">
                      <span className="block break-words font-semibold text-ink">
                        {(homeroom as any).teacher_name || homeroom.teacher_username}
                      </span>
                      <span className="block text-xs text-muted">Sees every student in {selectedGradeName}</span>
                    </span>
                  </div>
                ) : (
                  <select
                    aria-label="Homeroom teacher"
                    className={`${fieldClass} mt-2.5 w-full`}
                    defaultValue=""
                    onChange={(e) => {
                      const teacherId = Number(e.target.value);
                      if (!teacherId) return;
                      setHomeroomError(null);
                      homeroomCreate.mutate(
                        { teacher: teacherId, school_grade: gradeId, subject: null, is_homeroom: true },
                        {
                          onSuccess: () => setChangingHomeroomGradeId(null),
                          onError: (err) => setHomeroomError(apiErrorText(err, 'Could not set the homeroom teacher.')),
                        },
                      );
                    }}
                    disabled={homeroomCreate.isPending}>
                    <option value="">
                      {teacherProfiles.isLoading ? 'Loading teachers…' : 'Pick homeroom teacher…'}
                    </option>
                    {(teacherProfiles.data ?? []).map((t) => (
                      <option key={t.profile_id} value={t.profile_id}>{teacherName(t)}</option>
                    ))}
                  </select>
                )}
                {homeroomError && <p role="alert" className="mt-2 text-xs text-danger">{homeroomError}</p>}
              </div>
              <Button
                type="button"
                variant="ghost"
                className="mt-3 w-full"
                onClick={() => {
                  const g = gradesData?.grades?.find((x: any) => x.id === gradeId);
                  if (g) setRollNumbersGrade({ id: g.id, name: g.grade_name || g.name });
                }}>
                {`Roll numbers for ${selectedGradeName}`}
              </Button>
            </div>
          )}
        </PanelCard>

        {/* Subjects column */}
        <PanelCard
          className="min-h-[300px]"
          title={
            <div>
              <p className="mb-1 font-mono text-[11px] font-semibold uppercase tracking-[0.1em] text-muted">
                {selectedGradeName || 'No selection'}
              </p>
              <h2 className="font-display text-[22px] font-bold tracking-[-0.01em] text-ink">
                {isUniversity ? 'Academic years and courses' : 'Subjects & sections'}
              </h2>
            </div>
          }
          actions={
            <div className="flex flex-wrap items-center justify-end gap-2">
              {!isUniversity && gradeId != null && (
                <Button variant="danger-outline" size="sm"
                  onClick={() => setRemoveTarget({ kind: 'grade', gradeId, name: selectedGradeName || String(gradeId) })}>
                  Remove grade
                </Button>
              )}
              {!isUniversity && (
                <Button variant="ghost" icon={<ListFilter size={18} strokeWidth={1.8} aria-hidden />} aria-expanded={isPresetModalOpen} onClick={() => (isPresetModalOpen ? setIsPresetModalOpen(false) : loadPresets())} disabled={!gradeId}>
                  Load preset
                </Button>
              )}
              <Button
                variant="secondary"
                icon={<FaPlus />}
                onClick={() => { setSubjectError(null); setIsSubjectModalOpen(true); }}
                disabled={!gradeId}>
                {isUniversity ? 'Add year' : 'Add subject'}
              </Button>
            </div>
          }>
          {!gradeId && (
            <p className="mb-4 text-xs text-muted">Select {isUniversity ? 'a department' : 'a grade'} first.</p>
          )}

          {!isPresetModalOpen && importError && <p role="alert" className="mb-3 text-sm text-danger">{importError}</p>}

          {!isUniversity && gradeId != null && isPresetModalOpen && (
            <div className="mb-[18px] rounded-[16px] border border-line-strong bg-panel p-[18px]">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-semibold text-ink">Load preset subjects · {selectedGradeName}</p>
                  <p className="text-[13px] text-muted">
                    {selectedPresetIds.length === 0
                      ? 'Select subjects to import. Already-added ones are skipped.'
                      : `${selectedPresetIds.length} selected · already-added subjects are skipped`}
                  </p>
                </div>
                {presetSubjects.length > 0 && (
                  <TextLinkButton
                    disabled={selectableIds.length === 0}
                    onClick={() => {
                      if (allPresetsSelected) {
                        setSelectedPresetIds((cur) => cur.filter((id) => !selectableIds.includes(id)));
                      } else {
                        setSelectedPresetIds((cur) => Array.from(new Set([...cur, ...selectableIds])));
                      }
                    }}>
                    {allPresetsSelected ? 'Unselect all' : 'Select all'}
                  </TextLinkButton>
                )}
              </div>
              {presetLoading ? (
                <div className="flex justify-center py-8"><Spinner size="md" /></div>
              ) : presetError ? (
                <div role="alert" className="my-3.5 rounded-[12px] border border-danger/30 bg-danger/10 p-4 text-center">
                  <p className="text-sm font-medium text-danger">Couldn't load the preset subjects.</p>
                  <p className="mt-1 break-words text-xs text-muted">{presetError}</p>
                  <div className="mt-3">
                    <Button variant="ghost" size="sm" onClick={loadPresets}>Try again</Button>
                  </div>
                </div>
              ) : presetSubjects.length > 0 ? (
                <ul className="my-3.5 grid max-h-[320px] grid-cols-1 gap-2 overflow-y-auto sm:grid-cols-2 xl:grid-cols-3">
                  {presetSubjects.map((p) => {
                    const already = !!(
                      userInfo?.institution_id &&
                      Array.isArray(p.institutions) &&
                      p.institutions.includes(userInfo.institution_id)
                    );
                    const checked = selectedPresetIds.includes(p.id);
                    return (
                      <li key={p.id}>
                        <button
                          type="button"
                          role="checkbox"
                          aria-checked={already || checked}
                          disabled={already}
                          onClick={() => setSelectedPresetIds((cur) => (checked ? cur.filter((id) => id !== p.id) : [...cur, p.id]))}
                          className={`flex min-h-[44px] w-full items-center gap-2.5 rounded-[12px] border px-3 py-2.5 text-left text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                            already
                              ? 'cursor-default border-line-strong bg-panel text-muted'
                              : checked
                              ? 'border-navy bg-surface text-ink ring-1 ring-navy'
                              : 'border-line-strong bg-surface text-ink hover:border-line-hover'
                          }`}>
                          <span aria-hidden className={`grid h-[18px] w-[18px] flex-none place-items-center rounded-[5px] border-[1.5px] ${
                            already ? 'border-line bg-line text-muted' : checked ? 'border-navy bg-navy text-white' : 'border-control bg-surface text-white'
                          }`}>
                            {(already || checked) && <Check size={13} strokeWidth={3} />}
                          </span>
                          <span className="min-w-0 flex-1 break-words">{p.name}</span>
                          {already && <span className="font-mono text-[11px] font-semibold text-muted">Added</span>}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <div className="my-3.5">
                  <EmptyState compact icon={<FaBookOpen />} title="No preset subjects available" body="There is no preset list for this grade. Add subjects by name instead." />
                </div>
              )}
              {importError && <p role="alert" className="mb-3 text-sm text-danger">{importError}</p>}
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setIsPresetModalOpen(false)}>Cancel</Button>
                <Button onClick={importPresets} loading={addSubject.isPending} disabled={!gradeId || selectedPresetIds.length === 0}>
                  {selectedPresetIds.length > 0 ? `Import ${selectedPresetIds.length} selected` : 'Import selected'}
                </Button>
              </div>
            </div>
          )}

          {!isUniversity && gradeId != null && (
            <>
              <div className="mb-2.5 flex items-center justify-between gap-2.5">
                <span className={sectionLabel}>Sections</span>
                <TextLink to="/settings/sections">Manage sections →</TextLink>
              </div>
              <div className="mb-[22px] flex flex-wrap gap-2">
                {(sectionsQ.data?.sections ?? []).length === 0 ? (
                  <span className="text-xs text-muted">No sections yet.</span>
                ) : (
                  (sectionsQ.data?.sections ?? []).map((s) => {
                    const hr = personName(s.homeroom_teacher);
                    return (
                      <Link key={s.id} to="/settings/sections" className="inline-flex min-h-[40px] items-center gap-2 rounded-[12px] border border-line-strong bg-surface px-3 py-2 text-sm font-semibold text-ink hover:border-slate">
                        <span>Section {s.name}</span>
                        <span className="font-mono text-[11px] font-semibold text-muted">{s.student_count} students</span>
                        <span className="text-xs font-medium text-muted">· {hr || '[Unassigned]'}</span>
                      </Link>
                    );
                  })
                )}
              </div>
            </>
          )}

          {isUniversity ? (
            academicYears === null ? (
              <div className="flex justify-center py-6"><Spinner size="md" /></div>
            ) : academicYears.length ? (
              <ul className="space-y-3">
                {academicYears.map((y: AdminAcademicYear) => (
                  <li key={y.id} className="overflow-hidden rounded-[16px] border border-line">
                    <div className="flex items-center gap-2 pr-3">
                      <button
                        type="button"
                        aria-expanded={expandedYearId === y.id}
                        onClick={() => toggleYear(y)}
                        className="flex min-w-0 flex-1 items-center justify-between gap-3 px-4 py-3 text-left">
                        <span className="min-w-0 break-words font-semibold text-ink">{y.name}</span>
                        <span className="flex items-center gap-2 text-xs text-muted">
                          {loadingYearCourses === y.id && <Spinner />}
                          <FaChevronDown className={`transition-transform ${expandedYearId === y.id ? 'rotate-180' : ''}`} />
                        </span>
                      </button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        icon={<FaPlus />}
                        onClick={() => { setSelectedYearId(y.id); setCourseError(null); setIsCourseModalOpen(true); }}>
                        Add course
                      </Button>
                    </div>

                    {expandedYearId === y.id && (
                      <div className="border-t border-line px-4 py-3">
                        {coursesByYear[y.id] == null ? (
                          <p className="text-sm text-muted">Loading courses…</p>
                        ) : coursesByYear[y.id].length ? (
                          <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                            {coursesByYear[y.id].map((c) => (
                              <li key={c.id} className="flex items-center justify-between gap-2 rounded-[12px] border border-line px-3 py-2 text-sm">
                                <span className="min-w-0 break-words text-ink">{c.name}</span>
                                <Button type="button" variant="ghost" size="sm" onClick={() => goToChapters(c.id)}>
                                  Manage
                                </Button>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="py-2 text-center text-sm text-muted">No courses yet. Use Add course to create one.</p>
                        )}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState icon={<CalendarDays size={18} strokeWidth={1.8} />} title="No academic years yet" body="Add an academic year for this department to start adding courses." />
            )
          ) : subsLoading ? (
            <div className="flex justify-center py-6"><Spinner size="md" /></div>
          ) : subsData?.subjects?.length ? (
            <>
              <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
                <span className={sectionLabel}>{subsData.subjects.length} subjects</span>
                <span className="text-xs text-muted">Click a subject to open its chapters</span>
              </div>
              <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {subjectsPg.pageItems.map((s: any) => {
                  const teacher = teachersFor(s.id);
                  const subjectName = s.subject_name || s.name;
                  return (
                    <li key={s.id} className="relative">
                      <button type="button" aria-label={`Remove ${subjectName} from this grade`} title="Remove from this grade"
                        onClick={() => gradeId != null && setRemoveTarget({ kind: 'subject', gradeId, subjectId: s.id, name: subjectName, gradeName: selectedGradeName || '' })}
                        className="absolute right-2.5 top-2.5 z-10 grid h-8 w-8 place-items-center rounded-[9px] text-muted hover:bg-danger-bg hover:text-danger-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
                        <X size={16} aria-hidden />
                      </button>
                      <button
                        type="button"
                        onClick={() => goToChapters(s.id)}
                        className="flex h-full w-full flex-col items-stretch gap-2.5 rounded-[16px] border border-line bg-surface p-4 text-left transition hover:-translate-y-px hover:border-slate">
                        <span className="flex items-center justify-between gap-2">
                          <span className="min-w-0 break-words pr-8 text-left font-display text-base font-bold text-ink">{s.subject_name || s.name}</span>
                        </span>
                        <span className={`text-left text-[13px] ${teacher ? 'text-muted' : 'text-muted-2'}`}>
                          {typeof s.chapter_count === 'number' ? `${s.chapter_count} chapters · ` : ''}{teacher || 'No teacher yet'}
                        </span>
                        {typeof s.chapter_count === 'number' && typeof s.tagged_chapter_count === 'number' && (
                          <span className="flex items-center gap-2.5">
                            <Track
                              className="flex-1"
                              value={coverage(s) ?? 0}
                              tone={coverage(s) != null && (coverage(s) as number) < 60 ? 'accent' : 'slate'}
                              label={`${s.subject_name || s.name} questions coverage`}
                            />
                            <span className="font-mono text-[11px] font-semibold text-muted">
                              {coverage(s) == null ? 'No chapters' : `${coverage(s)}% tagged`}
                            </span>
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
                {subjectsPg.page === subjectsPg.pageCount && (
                  <li>
                    <button
                      type="button"
                      onClick={() => { setSubjectError(null); setIsSubjectModalOpen(true); }}
                      disabled={!gradeId}
                      className="flex h-full min-h-[132px] w-full flex-col items-center justify-center gap-1.5 rounded-[16px] border-[1.5px] border-dashed border-switch-off p-4 text-center text-sm font-semibold text-muted-2 transition-colors hover:border-navy hover:bg-surface-2 hover:text-ink disabled:opacity-50">
                      <FaPlus size={22} aria-hidden />
                      <span>Add subject</span>
                      <span className="text-xs font-normal text-muted">Search existing or create new</span>
                    </button>
                  </li>
                )}
              </ul>
              <PagedFooter from={subjectsPg.from} to={subjectsPg.to} total={subjectsPg.total} page={subjectsPg.page} pageCount={subjectsPg.pageCount} onPage={subjectsPg.setPage} className="mt-4" />
            </>
          ) : (
            <EmptyState icon={<FaBookOpen size={18} strokeWidth={1.8} />} title="No subjects yet" body="Add a subject for this grade, or load a preset list." />
          )}
        </PanelCard>
      </div>

      {/* --- Modals --- */}

      <Modal
        isOpen={isCourseModalOpen}
        onClose={() => setIsCourseModalOpen(false)}
        title="New course"
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setIsCourseModalOpen(false)}>Cancel</Button>
            <Button size="sm" type="submit" form="class-course-form" loading={courseBusy} disabled={!newCourseName.trim()}>
              Create course
            </Button>
          </>
        }>
        <form id="class-course-form" className="space-y-4" onSubmit={(e) => { e.preventDefault(); submitCourse(); }}>
          <FormField
            label="Course name"
            htmlFor="class-course-name"
            required
            hint={`This course will be added to ${academicYears?.find((y) => y.id === selectedYearId)?.name ?? 'the selected year'}.`}>
            <input
              id="class-course-name"
              className={fieldClass}
              value={newCourseName}
              maxLength={NAME_MAX}
              onChange={(e) => setNewCourseName(e.target.value)}
              placeholder="e.g. Data Structures, Economics 101"
              autoFocus
            />
          </FormField>
          {!newCourseName.trim() && <p className="text-xs text-muted">Enter a course name to continue.</p>}
          {courseError && <p role="alert" className="text-sm text-danger">{courseError}</p>}
        </form>
      </Modal>

      <Modal
        isOpen={isGradeModalOpen}
        onClose={() => setIsGradeModalOpen(false)}
        title={isUniversity ? 'New department' : 'New grade'}
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setIsGradeModalOpen(false)}>Cancel</Button>
            <Button
              size="sm"
              type="submit"
              form="class-grade-form"
              loading={addGrade.isPending || isUploading}
              disabled={!newGrade.trim() && !isUploading}>
              Create
            </Button>
          </>
        }>
        <form id="class-grade-form" className="space-y-4" onSubmit={(e) => { e.preventDefault(); submitGrade(); }}>
          <FormField label="Name" htmlFor="class-grade-name" required hint="Type to search existing ones or create a new one.">
            <div className="relative">
              <input
                id="class-grade-name"
                className={fieldClass}
                value={newGrade}
                maxLength={NAME_MAX}
                onChange={(e) => {
                  setNewGrade(e.target.value);
                  setGradeSearchTerm(e.target.value);
                }}
                placeholder="e.g. Science, Grade 10"
                autoComplete="off"
                autoFocus
              />
              {isGradeModalOpen && gradeSuggestions.length > 0 && (
                <ul className="absolute z-50 mt-1 max-h-48 w-full overflow-y-auto rounded-[12px] border border-line bg-surface p-1 shadow-card">
                  {gradeSuggestions.map((g: any) => (
                    <li key={g.id}>
                      <button
                        type="button"
                        className="block w-full rounded-[8px] px-3 py-2 text-left text-sm text-ink hover:bg-surface-2"
                        onClick={async () => {
                          setGradeError(null);
                          try {
                            await joinGrade.mutateAsync({ toBeValidated: token, gradeId: g.id });
                            setIsGradeModalOpen(false);
                            refetchGrades();
                            setNewGrade('');
                          } catch (e) {
                            setGradeError(apiErrorText(e, 'Could not add that grade. Try again.'));
                          }
                        }}>
                        {g.grade_name || g.name}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </FormField>
          {!newGrade.trim() && !isUploading && <p className="text-xs text-muted">Enter a name to continue.</p>}
          {gradeError && <p role="alert" className="text-sm text-danger">{gradeError}</p>}

          {isUniversity && (
            <div className="flex items-center justify-between gap-3 border-t border-line pt-4">
              <div className="min-w-0">
                <p className="text-sm font-medium text-ink">Or upload several departments</p>
                <p className="text-xs text-muted">A JSON file containing a list.</p>
              </div>
              <div>
                <input
                  type="file"
                  accept=".json"
                  id="upload-dept-json"
                  className="sr-only"
                  onChange={handleFileUpload}
                  disabled={isUploading}
                />
                <label
                  htmlFor="upload-dept-json"
                  className={`inline-flex min-h-[36px] cursor-pointer items-center rounded-[12px] border border-line-strong bg-surface px-3 py-1 text-xs text-ink hover:bg-surface-2 ${isUploading ? 'pointer-events-none opacity-50' : ''}`}>
                  {isUploading ? 'Uploading…' : 'Upload JSON'}
                </label>
              </div>
            </div>
          )}
        </form>
      </Modal>

      <Modal
        isOpen={isSubjectModalOpen}
        onClose={() => setIsSubjectModalOpen(false)}
        title={isUniversity ? 'New academic year' : 'New subject'}
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setIsSubjectModalOpen(false)}>Cancel</Button>
            <Button
              size="sm"
              type="submit"
              form="class-subject-form"
              loading={addSubject.isPending || yearBusy}
              disabled={subjectSaveDisabled || !gradeId}>
              Save
            </Button>
          </>
        }>
        <form id="class-subject-form" className="space-y-4" onSubmit={(e) => { e.preventDefault(); submitSubjectForm(); }}>
          <p className="rounded-[12px] border border-primary/20 bg-primary/5 px-3 py-2 text-sm text-ink">
            Adding to: <span className="break-words font-medium">{selectedGradeName || '-'}</span>
          </p>

          <FormField label={isUniversity ? 'Academic year name' : 'Subject name'} htmlFor="class-subject-name" required>
            <div className="relative">
              <input
                id="class-subject-name"
                className={fieldClass}
                value={isUniversity ? newAcademicYearName : searchTerm}
                maxLength={NAME_MAX}
                onChange={(e) => {
                  if (isUniversity) {
                    setNewAcademicYearName(e.target.value);
                  } else {
                    setSearchTerm(e.target.value);
                    setSelectedSubject(null);
                    setNewSubject(e.target.value);
                  }
                }}
                placeholder={isUniversity ? 'e.g. 2024/2025' : 'e.g. Mathematics, Biology'}
                autoComplete="off"
                autoFocus
              />
              {!isUniversity && searchTerm && filteredSuggestions.length > 0 && (
                <ul className="absolute z-50 mt-1 max-h-48 w-full overflow-y-auto rounded-[12px] border border-line bg-surface p-1 shadow-card">
                  {filteredSuggestions.map((s: any) => (
                    <li key={s.id}>
                      <button
                        type="button"
                        onClick={() => handleSubjectSelect(s)}
                        className="block w-full rounded-[8px] px-3 py-2 text-left text-sm text-ink hover:bg-surface-2">
                        {s.name}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </FormField>
          {subjectSaveDisabled && <p className="text-xs text-muted">Enter a name to continue.</p>}
          {subjectError && <p role="alert" className="text-sm text-danger">{subjectError}</p>}
        </form>
      </Modal>

      {rollNumbersGrade && (
        <RollNumbersModal
          gradeId={rollNumbersGrade.id}
          gradeName={rollNumbersGrade.name}
          isOpen
          onClose={() => setRollNumbersGrade(null)}
        />
      )}
      <RemoveDialog target={removeTarget} onClose={() => setRemoveTarget(null)}
        onRemoved={(t) => { if (t.kind === 'grade') setGradeId(null); refetchGrades(); refetchSubs(); }} />
    </div>
  );
};

export default ClassSetup;
